import {
  VOICE_AGENT_SIP_AUTO_ANSWER_TIMEOUT_MS,
  VOICE_CALL_ANSWERED_RTC_STATS_DELAY_MS,
  VOICE_CALL_ANSWERED_RTC_STATS_SECOND_DELAY_MS,
  VOICE_REMOTE_AUDIO_PLAYBACK_AFTER_ANSWER
} from '../../domain/voiceEventConstants';
import { VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID } from '../../infrastructure/config/voiceWebRtcConfig';
import {
  answerCurrentAgentCall,
  autoAnswerAgentSipInvite,
  connectAgentWebRtcClient,
  getCurrentAgentCall,
  logCurrentAgentRtcAudioStats,
  playRemoteMediaElement,
  scheduleAgentRemoteAudioPlayback,
  syncAgentRemoteAudioPlayback,
  waitForAgentSessionConfirmed
} from '../../infrastructure/webrtc/voiceAgentWebRtcAdapter';

export function isOutgoingCallDirection(direction) {
  const normalized = typeof direction === 'string' ? direction.trim().toLowerCase() : '';
  return normalized === 'outgoing' || normalized === 'outbound';
}

export function mergeActiveCallFromAnswered(baseCall, answered) {
  if (!baseCall) {
    return {
      callControlId: answered.callControlId,
      callSessionId: answered.callSessionId,
      callLegId: answered.callLegId,
      from: answered.from,
      to: answered.to,
      direction: answered.direction || 'outgoing',
      timestamp: answered.answeredAt || new Date().toISOString(),
      channel: answered.channel ?? 'unknown'
    };
  }

  return {
    ...baseCall,
    callSessionId: answered.callSessionId || baseCall.callSessionId,
    callLegId: answered.callLegId || baseCall.callLegId,
    from: answered.from || baseCall.from,
    to: answered.to || baseCall.to,
    direction: answered.direction || baseCall.direction,
    timestamp: answered.answeredAt || baseCall.timestamp,
    channel: answered.channel ?? baseCall.channel
  };
}

export function kickAgentRemoteAudioAfterCallAnswered() {
  scheduleAgentRemoteAudioPlayback(VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID, {
    attempts: VOICE_REMOTE_AUDIO_PLAYBACK_AFTER_ANSWER.attempts,
    intervalMs: VOICE_REMOTE_AUDIO_PLAYBACK_AFTER_ANSWER.intervalMs
  });
  const session = getCurrentAgentCall();
  if (!session) return;
  logCurrentAgentRtcAudioStats('call_answered');
  setTimeout(
    () => logCurrentAgentRtcAudioStats('call_answered+3s'),
    VOICE_CALL_ANSWERED_RTC_STATS_DELAY_MS
  );
  setTimeout(
    () => logCurrentAgentRtcAudioStats('call_answered+6s'),
    VOICE_CALL_ANSWERED_RTC_STATS_SECOND_DELAY_MS
  );
  waitForAgentSessionConfirmed(session, VOICE_AGENT_SIP_AUTO_ANSWER_TIMEOUT_MS)
    .then(() => {
      syncAgentRemoteAudioPlayback(VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID);
    })
    .catch(() => {
      syncAgentRemoteAudioPlayback(VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID);
    });
}

export async function ensureOutboundWebRtcMediaConnected(callControlId) {
  const existing = getCurrentAgentCall();
  if (existing && existing.status === 'confirmed') {
    playRemoteMediaElement(VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID);
    return;
  }

  if (callControlId) {
    await autoAnswerAgentSipInvite(callControlId, {
      timeoutMs: VOICE_AGENT_SIP_AUTO_ANSWER_TIMEOUT_MS
    });
    playRemoteMediaElement(VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID);
    return;
  }

  await connectAgentWebRtcClient();
  await answerCurrentAgentCall({ timeoutMs: VOICE_AGENT_SIP_AUTO_ANSWER_TIMEOUT_MS });
  playRemoteMediaElement(VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID);
}
